import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { translations } from "@/data/translations";
import { loadLocalOptions, loadLocalPayments } from "@/data/localAdmin";

const AppContext = createContext(null);

const LANG_KEY = "sommet_lang";
const CART_KEY = "sommet_cart";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AppProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) || "de"; } catch { return "de"; }
  });
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  // Admin-added colors/images for the configurator (case/dial/hands/strap),
  // loaded once for everyone so new options appear without a deploy.
  const [customOptions, setCustomOptions] = useState([]);
  const refreshCustomOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/custom-options`);
      setCustomOptions(res.data.options || []);
    } catch { setCustomOptions(loadLocalOptions()); /* no server: use options saved in this browser */ }
  }, []);
  useEffect(() => { refreshCustomOptions(); }, [refreshCustomOptions]);

  // Payment methods configured in the admin area (PayPal e-mail, IBAN, ...).
  const [paymentMethods, setPaymentMethods] = useState([]);
  const refreshPaymentMethods = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payment-methods`);
      setPaymentMethods(res.data.methods || []);
    } catch { setPaymentMethods(loadLocalPayments()); /* no server: use list saved in this browser */ }
  }, []);
  useEffect(() => { refreshPaymentMethods(); }, [refreshPaymentMethods]);

  useEffect(() => {
    try { localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* ignore */ }
  }, [cart]);

  const t = translations[lang];

  const addToCart = useCallback((item) => {
    setCart(prev => [...prev, { ...item, id: item.id || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }]);
    setCartOpen(true);
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart(prev => prev.filter(x => x.id !== id));
  }, []);

  const updateQuantity = useCallback((id, qty) => {
    setCart(prev => prev.map(x => x.id === id ? { ...x, quantity: Math.max(1, qty) } : x));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <AppContext.Provider value={{
      lang, setLang, t,
      cart, addToCart, removeFromCart, updateQuantity, clearCart,
      cartTotal, cartCount,
      cartOpen, setCartOpen,
      customOptions, refreshCustomOptions,
      paymentMethods, refreshPaymentMethods,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
};
